import React, { useState } from 'react';
import Card from '@/components/common/Card';
import Button from '@/components/common/Button';
import logger from '@/services/logger';

interface HelpWindowProps {
  windowId: string;
}

/**
 * Help window component
 * Provides documentation and assistance to users
 */
const HelpWindow: React.FC<HelpWindowProps> = ({ windowId }) => {
  const [activeSection, setActiveSection] = useState<string>('getting-started');
  
  // Log help section view
  const handleSectionChange = (section: string) => {
    setActiveSection(section);
    logger.info('Help section viewed', { 
      module: 'help', 
      data: { windowId, section } 
    });
  };

  return (
    <div className="h-full flex flex-col">
      <div className="flex-grow flex overflow-hidden">
        {/* Sidebar */}
        <div className="w-64 border-r border-gray-200 dark:border-gray-700 overflow-y-auto">
          <div className="p-4">
            <h3 className="text-lg font-medium mb-4">Help Topics</h3>
            <nav className="space-y-1">
              <HelpNavItem 
                id="getting-started"
                label="Getting Started"
                active={activeSection === 'getting-started'}
                onClick={() => handleSectionChange('getting-started')}
              />
              <HelpNavItem 
                id="windows"
                label="Window Management"
                active={activeSection === 'windows'}
                onClick={() => handleSectionChange('windows')}
              />
              <HelpNavItem 
                id="analysis"
                label="Email Analysis"
                active={activeSection === 'analysis'}
                onClick={() => handleSectionChange('analysis')}
              />
              <HelpNavItem 
                id="steps"
                label="Analysis Steps"
                active={activeSection === 'steps'}
                onClick={() => handleSectionChange('steps')}
              />
              <HelpNavItem 
                id="settings"
                label="Settings"
                active={activeSection === 'settings'}
                onClick={() => handleSectionChange('settings')}
              />
              <HelpNavItem 
                id="shortcuts"
                label="Keyboard Shortcuts"
                active={activeSection === 'shortcuts'}
                onClick={() => handleSectionChange('shortcuts')}
              />
              <HelpNavItem 
                id="faq"
                label="FAQ"
                active={activeSection === 'faq'}
                onClick={() => handleSectionChange('faq')}
              />
            </nav>
          </div>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeSection === 'getting-started' && <GettingStartedContent />}
          {activeSection === 'windows' && <WindowsContent />}
          {activeSection === 'analysis' && <AnalysisContent />}
          {activeSection === 'steps' && <StepsContent />}
          {activeSection === 'settings' && <SettingsContent />}
          {activeSection === 'shortcuts' && <ShortcutsContent />}
          {activeSection === 'faq' && <FAQContent />}
        </div>
      </div>
      
      <div className="p-2 border-t border-gray-200 dark:border-gray-700 flex justify-end">
        <Button 
          variant="secondary" 
          size="small"
          onClick={() => window.open('https://github.com/yourusername/phishphind', '_blank')}
        >
          Visit Documentation
        </Button>
      </div>
    </div>
  );
};

// Helper components
interface HelpNavItemProps {
  id: string;
  label: string;
  active: boolean;
  onClick: () => void;
}

const HelpNavItem: React.FC<HelpNavItemProps> = ({ id, label, active, onClick }) => (
  <button
    className={`block w-full text-left px-3 py-2 rounded-md transition-colors text-sm ${
      active
        ? 'bg-primary bg-opacity-10 text-primary font-medium'
        : 'hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300'
    }`}
    onClick={onClick}
  >
    {label}
  </button>
);

// Content components for different sections
const GettingStartedContent: React.FC = () => (
  <div className="space-y-4">
    <h2 className="text-2xl font-bold mb-4">Getting Started with PhishPhind</h2>
    
    <p className="text-gray-700 dark:text-gray-300">
      PhishPhind is a multi-step LLM prompting tool designed to help detect and analyze potential phishing emails. 
      This guide will help you get started with the basic features of the application.
    </p>
    
    <h3 className="text-xl font-semibold mt-6 mb-2">Key Features</h3>
    
    <Card className="mb-4">
      <Card.Body>
        <div className="flex items-start">
          <div className="mr-4 text-primary">
            <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
              <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
              <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
            </svg>
          </div>
          <div>
            <h4 className="font-semibold text-lg">Email Analysis</h4>
            <p className="text-gray-600 dark:text-gray-400">
              Upload or paste email content for thorough analysis by our multi-step LLM system.
            </p>
          </div>
        </div>
      </Card.Body>
    </Card>
    
    <Card className="mb-4">
      <Card.Body>
        <div className="flex items-start">
          <div className="mr-4 text-primary">
            <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
              <path d="M7 3a1 1 0 000 2h6a1 1 0 100-2H7zM4 7a1 1 0 011-1h10a1 1 0 110 2H5a1 1 0 01-1-1zM2 11a2 2 0 012-2h12a2 2 0 012 2v4a2 2 0 01-2 2H4a2 2 0 01-2-2v-4z" />
            </svg>
          </div>
          <div>
            <h4 className="font-semibold text-lg">Customizable Steps</h4>
            <p className="text-gray-600 dark:text-gray-400">
              Create and modify analysis steps to tailor the detection process to your needs.
            </p>
          </div>
        </div>
      </Card.Body>
    </Card>
    
    <Card className="mb-4">
      <Card.Body>
        <div className="flex items-start">
          <div className="mr-4 text-primary">
            <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
              <path d="M7 9a2 2 0 012-2h6a2 2 0 012 2v6a2 2 0 01-2 2H9a2 2 0 01-2-2V9z" />
              <path d="M5 3a2 2 0 00-2 2v6a2 2 0 002 2V5h8a2 2 0 00-2-2H5z" />
            </svg>
          </div>
          <div>
            <h4 className="font-semibold text-lg">Results Export</h4>
            <p className="text-gray-600 dark:text-gray-400">
              Export analysis results in various formats for sharing or documentation.
            </p>
          </div>
        </div>
      </Card.Body>
    </Card>
    
    <h3 className="text-xl font-semibold mt-6 mb-2">Quick Start Guide</h3>
    
    <ol className="list-decimal pl-6 space-y-2">
      <li className="text-gray-700 dark:text-gray-300">Click on "Begin Analysis" from the homepage</li>
      <li className="text-gray-700 dark:text-gray-300">Paste your email content into the text area</li>
      <li className="text-gray-700 dark:text-gray-300">Select an analysis type</li>
      <li className="text-gray-700 dark:text-gray-300">Click "Analyze Email" to start the process</li>
      <li className="text-gray-700 dark:text-gray-300">View the results and export if needed</li>
    </ol>
  </div>
);

const WindowsContent: React.FC = () => (
  <div className="space-y-4">
    <h2 className="text-2xl font-bold mb-4">Window Management</h2>
    
    <p className="text-gray-700 dark:text-gray-300">
      PhishPhind uses a flexible window system that allows you to organize your workspace efficiently.
      Learn how to manage windows, resize them, and save layouts.
    </p>
    
    <h3 className="text-xl font-semibold mt-6 mb-2">Working with Windows</h3>
    
    <div className="space-y-4">
      <h4 className="font-medium">Window Controls</h4>
      <ul className="list-disc pl-6 space-y-1">
        <li>Minimize: Collapses the window to the taskbar</li>
        <li>Maximize: Expands the window to fill the available space</li>
        <li>Close: Removes the window from view</li>
        <li>Drag: Click and hold the window title bar to move it</li>
        <li>Resize: Click and drag any window edge or corner to resize</li>
      </ul>
      
      <h4 className="font-medium">Window Layouts</h4>
      <p>
        You can save your preferred window arrangements as layouts and apply them later.
        Use the Window Manager to create and manage layouts.
      </p>
      
      <h4 className="font-medium">Window Types</h4>
      <ul className="list-disc pl-6 space-y-1">
        <li><strong>Email Editor:</strong> For composing and editing email content</li>
        <li><strong>Analysis Results:</strong> Displays phishing analysis results</li>
        <li><strong>Settings:</strong> Configure application preferences</li>
        <li><strong>Help:</strong> Access documentation and assistance</li>
      </ul>
    </div>
    
    <div className="mt-4 p-4 bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-700 rounded-md">
      <h4 className="font-medium text-yellow-800 dark:text-yellow-300">Pro Tip</h4>
      <p className="text-yellow-700 dark:text-yellow-200">
        Use the "Arrange Windows" options in the Window Manager to quickly organize your windows in different patterns like cascade, tile, horizontal, or vertical.
      </p>
    </div>
  </div>
);

// Placeholder components for other sections
const AnalysisContent: React.FC = () => (
  <div>
    <h2 className="text-2xl font-bold mb-4">Email Analysis</h2>
    <p className="text-gray-700 dark:text-gray-300">
      Content about email analysis features coming soon...
    </p>
  </div>
);

const StepsContent: React.FC = () => (
  <div>
    <h2 className="text-2xl font-bold mb-4">Analysis Steps</h2>
    <p className="text-gray-700 dark:text-gray-300">
      Content about customizing analysis steps coming soon...
    </p>
  </div>
);

const SettingsContent: React.FC = () => (
  <div>
    <h2 className="text-2xl font-bold mb-4">Settings</h2>
    <p className="text-gray-700 dark:text-gray-300">
      Content about application settings coming soon...
    </p>
  </div>
);

const ShortcutsContent: React.FC = () => (
  <div>
    <h2 className="text-2xl font-bold mb-4">Keyboard Shortcuts</h2>
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
        <thead>
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Action</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Shortcut (Windows/Linux)</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Shortcut (Mac)</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
          <tr>
            <td className="px-6 py-2">New Window</td>
            <td className="px-6 py-2 font-mono text-sm">Ctrl + N</td>
            <td className="px-6 py-2 font-mono text-sm">⌘ + N</td>
          </tr>
          <tr>
            <td className="px-6 py-2">Close Window</td>
            <td className="px-6 py-2 font-mono text-sm">Ctrl + W</td>
            <td className="px-6 py-2 font-mono text-sm">⌘ + W</td>
          </tr>
          <tr>
            <td className="px-6 py-2">Minimize Window</td>
            <td className="px-6 py-2 font-mono text-sm">Alt + M</td>
            <td className="px-6 py-2 font-mono text-sm">Option + M</td>
          </tr>
          <tr>
            <td className="px-6 py-2">Maximize Window</td>
            <td className="px-6 py-2 font-mono text-sm">Ctrl + M</td>
            <td className="px-6 py-2 font-mono text-sm">⌘ + M</td>
          </tr>
          <tr>
            <td className="px-6 py-2">Next Window</td>
            <td className="px-6 py-2 font-mono text-sm">Alt + Tab</td>
            <td className="px-6 py-2 font-mono text-sm">Option + Tab</td>
          </tr>
          <tr>
            <td className="px-6 py-2">Previous Window</td>
            <td className="px-6 py-2 font-mono text-sm">Alt + Shift + Tab</td>
            <td className="px-6 py-2 font-mono text-sm">Option + Shift + Tab</td>
          </tr>
          <tr>
            <td className="px-6 py-2">Focus Window 1-9</td>
            <td className="px-6 py-2 font-mono text-sm">Alt + (1-9)</td>
            <td className="px-6 py-2 font-mono text-sm">Option + (1-9)</td>
          </tr>
          <tr>
            <td className="px-6 py-2">Cascade Windows</td>
            <td className="px-6 py-2 font-mono text-sm">Alt + Shift + ↓</td>
            <td className="px-6 py-2 font-mono text-sm">Option + Shift + ↓</td>
          </tr>
          <tr>
            <td className="px-6 py-2">Tile Windows</td>
            <td className="px-6 py-2 font-mono text-sm">Alt + Shift + ↑</td>
            <td className="px-6 py-2 font-mono text-sm">Option + Shift + ↑</td>
          </tr>
          <tr>
            <td className="px-6 py-2">Arrange Horizontally</td>
            <td className="px-6 py-2 font-mono text-sm">Alt + Shift + →</td>
            <td className="px-6 py-2 font-mono text-sm">Option + Shift + →</td>
          </tr>
          <tr>
            <td className="px-6 py-2">Arrange Vertically</td>
            <td className="px-6 py-2 font-mono text-sm">Alt + Shift + ←</td>
            <td className="px-6 py-2 font-mono text-sm">Option + Shift + ←</td>
          </tr>
          <tr>
            <td className="px-6 py-2">Save Layout</td>
            <td className="px-6 py-2 font-mono text-sm">Alt + Shift + S</td>
            <td className="px-6 py-2 font-mono text-sm">Option + Shift + S</td>
          </tr>
          <tr>
            <td className="px-6 py-2">Apply Layout</td>
            <td className="px-6 py-2 font-mono text-sm">Alt + Shift + L</td>
            <td className="px-6 py-2 font-mono text-sm">Option + Shift + L</td>
          </tr>
          <tr>
            <td className="px-6 py-2">Reopen Last Closed Window</td>
            <td className="px-6 py-2 font-mono text-sm">Ctrl + Z</td>
            <td className="px-6 py-2 font-mono text-sm">⌘ + Z</td>
          </tr>
          <tr>
            <td className="px-6 py-2">Toggle Dark Mode</td>
            <td className="px-6 py-2 font-mono text-sm">Ctrl + Shift + D</td>
            <td className="px-6 py-2 font-mono text-sm">⌘ + Shift + D</td>
          </tr>
          <tr>
            <td className="px-6 py-2">Open Settings</td>
            <td className="px-6 py-2 font-mono text-sm">Ctrl + ,</td>
            <td className="px-6 py-2 font-mono text-sm">⌘ + ,</td>
          </tr>
          <tr>
            <td className="px-6 py-2">Open Help</td>
            <td className="px-6 py-2 font-mono text-sm">F1</td>
            <td className="px-6 py-2 font-mono text-sm">F1</td>
          </tr>
        </tbody>
      </table>
    </div>
    
    <div className="mt-6">
      <h3 className="text-lg font-semibold mb-2">Notes:</h3>
      <ul className="list-disc pl-5 space-y-1">
        <li>Shortcuts may vary slightly depending on your operating system</li>
        <li>On Mac, most Ctrl shortcuts use the Command (⌘) key instead</li>
        <li>Alt key on Windows/Linux corresponds to Option key on Mac</li>
        <li>Some shortcuts may be intercepted by your browser or operating system</li>
      </ul>
    </div>
  </div>
);

const FAQContent: React.FC = () => (
  <div className="space-y-6">
    <h2 className="text-2xl font-bold mb-4">Frequently Asked Questions</h2>
    
    <div className="space-y-4">
      <div>
        <h3 className="font-semibold text-lg">What is PhishPhind?</h3>
        <p className="text-gray-700 dark:text-gray-300">
          PhishPhind is a multi-step LLM prompting tool designed to help detect and analyze potential phishing emails.
          It uses advanced AI techniques to identify suspicious elements in email content.
        </p>
      </div>
      
      <div>
        <h3 className="font-semibold text-lg">Is my email data secure?</h3>
        <p className="text-gray-700 dark:text-gray-300">
          Yes, PhishPhind processes all data locally in your browser by default. Your email content is not stored on any server
          unless you explicitly configure the application to use a remote API. API calls are made with secure connections.
        </p>
      </div>
      
      <div>
        <h3 className="font-semibold text-lg">Can I customize the analysis steps?</h3>
        <p className="text-gray-700 dark:text-gray-300">
          Absolutely! PhishPhind allows you to create, edit, and customize analysis steps to tailor the detection 
          process to your specific needs and threat landscape.
        </p>
      </div>
      
      <div>
        <h3 className="font-semibold text-lg">What LLM providers are supported?</h3>
        <p className="text-gray-700 dark:text-gray-300">
          PhishPhind currently supports OpenAI, Anthropic, and Google AI as LLM providers. 
          You can configure your API keys in the Settings window.
        </p>
      </div>
      
      <div>
        <h3 className="font-semibold text-lg">How do I export analysis results?</h3>
        <p className="text-gray-700 dark:text-gray-300">
          After completing an analysis, you can export the results using the "Export Results" button in the 
          Results window. Supported formats include PDF, JSON, and HTML.
        </p>
      </div>
    </div>
  </div>
);

export default HelpWindow; 